import React, { FC } from "react";
import { Divider, Stack, Typography } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";

import { Colors } from "@src/gatsby-theme-material-ui-top-layout/colors";
import { NumberCardProps, NumberCardRowsProps } from "./types";

interface Props extends NumberCardProps {
  isSmall: boolean;
  last?: boolean;
  additionalPaddingX?: number;
}

const NumberCard: FC<Props> = ({
  title,
  content,
  isSmall,
  last = false,
  additionalPaddingX = 0,
}) => {
  return (
    <>
      <Stack
        textAlign="center"
        gap="5px"
        sx={{
          maxWidth: "312px",
          height: "100%",
          margin: isSmall ? "24px" : `30px 20px`,
          flexGrow: "1",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontSize: "56px",
            lineHeight: "64px",
            fontWeight: "700",
            color: Colors.PrimaryContrast,
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: Colors.PrimaryContrast,
            fontSize: "16px",
            lineHeight: "22px",
            paddingX: `${additionalPaddingX}px`,
          }}
        >
          {content}
        </Typography>
      </Stack>
      {!last && (
        <Divider
          orientation={isSmall ? "horizontal" : "vertical"}
          flexItem
          sx={{
            borderColor: "rgba(255, 255, 255, 0.3)",
            borderWidth: "1px",
          }}
        />
      )}
    </>
  );
};

const NumberCardRows: FC<NumberCardRowsProps> = ({ header, cards }) => {
  const isSmall = useMediaQuery("(max-width:768px)");
  const cardCount = cards?.length || 0;

  // TODO: Fix to handle arbitrary number of cards
  let contWidth = cards?.length === 3 ? "100%" : "66%";
  let cardWidth = 100 / (cards?.length || 1);

  if (cards?.length === 1) {
    contWidth = "40%";
  }

  if (isSmall) {
    contWidth = "100%";
    cardWidth = 100;
  }

  return (
    <Stack textAlign="center" alignItems="center">
      {header && <Typography variant="h2">{header}</Typography>}
      <Stack
        direction={isSmall ? "column" : "row"}
        justifyContent="center"
        alignItems={isSmall ? "center" : "flex-start"}
        mt={3}
        width={contWidth}
        sx={{
          paddingY: 3,
          background:
            "linear-gradient(95.58deg, #35124D 0%, #3B1556 16.04%, #3D195F 35.62%, #281A52 55.93%, #111E48 76.22%, #195888 100%)",
          borderRadius: "15px",
          "& div": {
            borderLeft: "1px thin red",
          },
        }}
      >
        {cards.map((card: NumberCardProps, idx: number) => {
          return (
            <NumberCard
              {...card}
              isSmall={isSmall}
              last={idx === cardCount - 1}
              key={`number-card-${idx}`}
            />
          );
        })}
      </Stack>
    </Stack>
  );
};

export default NumberCardRows;
